import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { AesRuleDef } from 'api/AesAnalyzerAPI';

interface IAesBulkClearModalProps {
  handleClose: () => void;
  /** Every rule, in the analyzer's order. Only those with findings are listed. */
  rules: AesRuleDef[];
  /** Open findings per rule id on this run. */
  openCounts: Record<string, number>;
  /** User-cleared findings per rule id on this run. */
  clearedCounts: Record<string, number>;
  onSubmit: (rule: string, clear: boolean) => Promise<void>;
  isLoading: boolean;
}

/**
 * Bulk clear by finding (spec "Bulk-clear by rule"): dismiss every open
 * finding of one kind on the run in one action, or bring back every one that
 * was cleared. Same modal shell as the AES filter modal and the ACE Analyzer's
 * bulk actions. Each dismissal is logged on its line with who and when.
 */
const AesBulkClearModal: React.FC<IAesBulkClearModalProps> = ({
  handleClose,
  rules,
  openCounts,
  clearedCounts,
  onSubmit,
  isLoading,
}) => {
  const [clear, setClear] = useState(true);
  const [rule, setRule] = useState<string | null>(null);

  const counts = clear ? openCounts : clearedCounts;
  const options = rules.filter((r) => (counts[r.id] || 0) > 0);
  const selected = options.find((r) => r.id === rule) || null;
  const n = selected ? counts[selected.id] || 0 : 0;

  const switchAction = (next: boolean) => {
    setClear(next);
    setRule(null);
  };

  return (
    <div
      className='audit-add-modal-cont'
      style={{ maxWidth: '640px', width: '100%' }}
    >
      <div className='audit-add-modal-cont-head'>
        <div className='audit-add-modal-head-cont-change'>
          <div className='audit-add-modal-head-cont-icon'>
            <LayersIcon
              sx={{ color: '#4895EF', width: '24px', height: '24px' }}
            />
          </div>
          <div className='audit-add-modal-head-cont-supporting'>
            <h1>Bulk Actions</h1>
            <p>
              Clear every open finding of one kind on this run at once, or
              restore the ones that were cleared.
            </p>
          </div>
        </div>
        <div
          className='audit-add-modal-head-close'
          onClick={() => !isLoading && handleClose()}
          style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M18 6L6 18M6 6L18 18'
              stroke='#85888E'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      <div
        className='audit-add-modal-cont-body'
        style={{
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          className='data-segmented-control'
          style={{ alignSelf: 'flex-start' }}
        >
          <button
            type='button'
            className={`data-segment-btn ${clear ? 'active' : ''}`}
            onClick={() => switchAction(true)}
          >
            Clear findings
          </button>
          <button
            type='button'
            className={`data-segment-btn ${!clear ? 'active' : ''}`}
            onClick={() => switchAction(false)}
          >
            Restore cleared findings
          </button>
        </div>

        {options.length ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              maxHeight: 360,
              overflowY: 'auto',
              paddingRight: 4,
            }}
          >
            {options.map((r) => {
              const isSelected = r.id === rule;
              return (
                <div
                  key={r.id}
                  className='bill-container-fit-screen-content-row-checkbox'
                  onClick={() => setRule(r.id)}
                  style={{
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <div className='bill-container-fit-screen-content-row-input'>
                    <div
                      className={`bill-container-fit-screen-content-row-input-icon ${
                        isSelected ? 'selected' : ''
                      }`}
                    >
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                          isSelected ? 'selected' : ''
                        }`}
                      />
                    </div>
                  </div>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox-text'
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {/* Flag name only: severity varies per line for some
                        findings, so the rule's default would mislead here. */}
                    <span style={{ fontWeight: 500, textAlign: 'left' }}>
                      {r.flag}
                    </span>
                    <span
                      style={{
                        color: 'var(--text-secondary)',
                        fontSize: 13,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {(counts[r.id] || 0).toLocaleString()}{' '}
                      {clear ? 'open' : 'cleared'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
            {clear
              ? 'No open findings on this run.'
              : 'No cleared findings to restore on this run.'}
          </p>
        )}

        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: 13,
            margin: 0,
            minHeight: 20,
          }}
        >
          {selected
            ? clear
              ? `Clears ${n.toLocaleString()} open "${selected.flag}" finding${
                  n === 1 ? '' : 's'
                }. Each is logged with your name and the time, and can be restored later.`
              : `Restores ${n.toLocaleString()} cleared "${
                  selected.flag
                }" finding${n === 1 ? '' : 's'}, so they count as open again.`
            : 'Choose a finding.'}
        </p>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      <div className='audit-modal-cont-footer'>
        <div
          className='audit-modal-cont-footer-content'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={() => !isLoading && handleClose()}
            style={{ width: '150px', padding: '0 24px', flex: 'none' }}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Cancel
            </div>
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={() =>
              selected && !isLoading && onSubmit(selected.id, clear)
            }
            style={{
              width: '170px',
              padding: '0 24px',
              flex: 'none',
              opacity: selected && !isLoading ? 1 : 0.6,
              cursor: selected && !isLoading ? 'pointer' : 'not-allowed',
            }}
          >
            <div
              className='audit-modal-cont-footer-content-btn-text'
              style={{
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {isLoading && (
                <CircularProgress
                  style={{ width: '16px', height: '16px', marginRight: '8px' }}
                  color='inherit'
                />
              )}
              {clear ? 'Clear Findings' : 'Restore Findings'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AesBulkClearModal;
